import { PaceTaskType } from '@symfa-inc/providence-verizon-types';
import { createSelector } from 'reselect';
import {
  LeasingDetailsLeaseAuditPACETaskKeys,
  LeasingDetailsWorkflowEngineeringPanelPACETaskKeys,
  LeasingDetailsWorkflowLeasingPanelPACETaskKeys,
  LeasingDetailsWorkflowStructuralPanelPACETaskKeys,
  PaceTaskDatesNarrowed,
} from '@models/types';
import { AppState } from '../reducers';
import { LeasingState } from '../reducers/leasing.reducer';
import { PaceTasksSelectors } from './pace-tasks.selectors';

const leasingState = (state: AppState): LeasingState => state.leasing;
const appState = (state: AppState): AppState => state;

export namespace LeasingSelectors {
  // common
  export const getProjectId = createSelector(
    [leasingState],
    ({ currentProject, tabData: { summary } }: LeasingState) =>
      summary?.id || currentProject?.id,
  );

  export const getProjectStatus = createSelector(
    [leasingState],
    ({ tabData: { summary } }: LeasingState) =>
      summary?.project?.projectStatusId,
  );

  // specific
  export const getLeasingBrowseInfo = createSelector(
    [leasingState],
    (leasing: LeasingState) => leasing.leasingBrowseData,
  );

  export const getLeasingDetailsSummaryData = createSelector(
    [leasingState],
    ({ tabData: { summary } }: LeasingState) => ({
      siteInfo: summary.site,
      projectInfo: summary.project,
      leasingInfo: {
        ...summary.leasing,
        projectBundleID: summary.project?.projectBundleID,
      },
    }),
  );

  export const getLeasingDetailsWorkflowEngineeringPanel = createSelector(
    [
      leasingState,
      PaceTasksSelectors.getMainPaceTaskDates(
        PaceTaskType.ActualDate,
        'mountMappingOrdered',
        'mountMappingReceived',
        'mountAnalysisOrdered',
        'failingMountAnalysisReceived',
        'passingMountAnalysisReceived',
        'passingMountAnalysisReviewedLeasing',
        'prelimCDsOrdered',
        'prelimCDsReceived',
        'finalCDsReviewedLeasing',
        'finalCDsReceived',
        'prelimCDsApprovedLeasing',
      ),
    ],
    (
      { tabData: { workflow } }: LeasingState,
      paceTasks: PaceTaskDatesNarrowed<LeasingDetailsWorkflowEngineeringPanelPACETaskKeys>,
    ) => ({
      ...workflow?.engineering,
      ...paceTasks,
    }),
  );

  export const getLeasingDetailsWorkflowLeasingPanel = createSelector(
    [
      leasingState,
      PaceTasksSelectors.getMainPaceTaskDates(
        PaceTaskType.ActualDate,
        'leaseAuditComplete',
        'SOFIssued',
        'coloAppDrafted',
        'coloAppApproved',
        'coloAppSubmitted',
        'PALOrNPAReceived',
        'leaseDraftReceived',
        'LMPSSubmitted',
        'LMPSApproved',
        'leaseOrAmendmentFullyExecuted',
        'NTPApproved',
        'NTPRequested',
      ),
    ],
    (
      { tabData: { workflow } }: LeasingState,
      paceTasks: PaceTaskDatesNarrowed<LeasingDetailsWorkflowLeasingPanelPACETaskKeys>,
    ) => ({
      ...workflow?.leasing,
      ...paceTasks,
    }),
  );

  export const getLeasingDetailsWorkflowStructuralPanel = createSelector(
    [
      leasingState,
      PaceTasksSelectors.getMainPaceTaskDates(
        PaceTaskType.ActualDate,
        'structuralOrdered',
        'passingStructuralReceived',
        'failingStructuralReceived',
        'modDesignComplete',
        'IBMApproved',
        'modConstructionStart',
        'modConstructionComplete',
      ),
    ],
    (
      { tabData: { workflow } }: LeasingState,
      paceTasks: PaceTaskDatesNarrowed<LeasingDetailsWorkflowStructuralPanelPACETaskKeys>,
    ) => ({
      ...workflow?.structural,
      isStructuralAdequate: workflow?.structural?.isStructuralAdequate ?? false,
      ...paceTasks,
    }),
  );

  export const getLeaseAuditData = createSelector(
    [
      leasingState,
      PaceTasksSelectors.getMainPaceTaskDates(
        PaceTaskType.ActualDate,
        'leaseAuditComplete',
      ),
    ],
    (
      { tabData: { leaseAudit } }: LeasingState,
      {
        leaseAuditComplete,
      }: PaceTaskDatesNarrowed<LeasingDetailsLeaseAuditPACETaskKeys>,
    ) => ({
      ...leaseAudit,
      leaseAuditComplete,
    }),
  );

  export const getLeasingSummary = createSelector(
    [leasingState],
    ({
      leasingInformation,
      leasingSite,
      currentProject,
      leasingAgents,
    }: LeasingState) => ({
      leasingInformation: {
        ...leasingInformation,
        leaseAudit: {
          ...leasingInformation?.leaseAudit,
          isMostRecentAdequate:
            leasingInformation?.leaseAudit?.isMostRecentAdequate ?? false,
        },
      },
      leasingSite,
      currentProject,
      leasingAgents,
    }),
  );

  export const getLeasingSite = createSelector(
    [leasingState],
    ({ tabData: { summary } }: LeasingState) => {
      const site = summary?.site;

      return site;
    },
  );

  export const getLeasingEquipmentSectionData = createSelector(
    [appState],
    ({
      equipment: { allUsedEquipments },
      leasing: {
        leasingEquipmentData: {
          curPriorTableData,
          scopingTableData,
          curFinalTableData,
        },
      },
    }: AppState) => ({
      allUsedEquipments,
      curPriorTableData,
      scopingTableData,
      curFinalTableData,
    }),
  );

  export const getLeasingEquipmentData = createSelector(
    [leasingState, getProjectId],
    (
      {
        equipmentTypeNames,
        leasingEquipmentData: {
          curPriorTableData,
          curFinalTableData,
          scopingTableData,
          priorTableData,
          finalTableData,
          scopingNotes,
          priorLeasingNote,
          finalLeasingNote,
        },
      }: LeasingState,
      projectId,
    ) => ({
      id: projectId,
      equipmentTypeNames,
      curPriorTableData,
      curFinalTableData,
      priorTableData,
      finalTableData,
      scopingTableData,
      scopingNotes,
      priorLeasingNote,
      finalLeasingNote,
    }),
  );

  export const getProjectType = createSelector(
    [leasingState],
    (state: LeasingState) => state.tabData.summary?.project?.projectTypeId,
  );

  export const getLeasingJurisdictionName = createSelector(
    [leasingState],
    (state: LeasingState) => state.tabData.summary?.site?.jurisdictionName,
  );

  export const isFetching = createSelector(
    [leasingState],
    (state: LeasingState) => state.isFetching,
  );

  export const getTabLoadingState = (tabName: string) =>
    createSelector(
      [leasingState],
      (state: LeasingState) => state.loadingTabs[tabName] || false,
    );

  export const getTabLoadedState = (tabName: string) =>
    createSelector(
      [leasingState],
      (state: LeasingState) => state.loadedTabs[tabName] || false,
    );
}
